import { useEffect } from 'react'

import { usePromise, PromiseStateConfig } from './promiseState'
import { useDispatch } from 'react-redux'
import { queueNotification } from '~/shared/notifications/actions'
import { logger } from '~/shared/lib/logger'

export interface ResolveNotification {
  success?: string
  error?: string
}
export type UseMutationConfig = PromiseStateConfig & ResolveNotification

/** The subset of a promise-state result this hook reads; `useMutation` results also satisfy it. */
type PartialPromiseState = Partial<ReturnType<typeof usePromise>>
/**
 * This is an abstraction to handle both surfacing async operation feedback
 * to a user, and for reporting in the case of a failed call.
 * @param mutation Partial response from ~/legacy/core's useMutation including error, result, or both
 * @param notification Partial messaging information, making feedback optional for success and failure states
 */
export const useResolvedPromise = (
  mutation: PartialPromiseState,
  notification?: ResolveNotification,
) => {
  const dispatch = useDispatch()

  // Keyed on settling rather than on the result alone: a handler called again stores an equal
  // result, which would not re-trigger the effects below.
  const resolved = !mutation.loading

  // Report errors via apm and show notification if applicable
  useEffect(() => {
    if (resolved && mutation.error) {
      if (notification?.error) {
        dispatch(
          queueNotification({
            variant: 'error',
            message: notification.error,
          }),
        )
      }
      logger.error('[Query] request failed', mutation.error)
    }
  }, [resolved, mutation.error, notification?.error, dispatch])

  // Show success notification if applicable
  useEffect(() => {
    // Checking typeof here in case of falsy return values
    if (resolved && typeof mutation.result !== 'undefined' && notification?.success) {
      dispatch(
        queueNotification({
          variant: 'success',
          message: notification.success,
        }),
      )
    }
  }, [resolved, mutation.result, notification?.success, dispatch])
}
